import React, { memo, useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import Select, { components } from 'react-select';
import { FormGroup, Input, Label } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown } from '@fortawesome/free-solid-svg-icons';

import OrgsMapContext from '../../Context/OrgsMapContext';

const DropdownIndicator = (props) => (
  <components.DropdownIndicator {...props}>
    <FontAwesomeIcon icon={faCaretDown} className="text-muted" size="xs" />
  </components.DropdownIndicator>
);

const Menu = (props) => {
  const {
    selectProps: { handleEcosystemToggle, includeChildren, mobileView },
  } = props;
  const handleToggle = useCallback(() => {
    handleEcosystemToggle(mobileView);
  }, [handleEcosystemToggle, mobileView]);

  return (
    <components.Menu {...props}>
      {props.children}
      <hr className="mb-0 mt-2" />
      <div className="p-2">
        <FormGroup check className="p-0">
          <Label
            check
            className="d-flex align-items-center"
            htmlFor="ecosystem-checkbox"
          >
            <Input
              checked={includeChildren}
              className="eight-tenth-text m-0 position-static"
              id="ecosystem-checkbox"
              onChange={handleToggle}
              style={{ cursor: 'pointer' }}
              type="checkbox"
            />
            <span className="ml-1">Show Ecosystem Events</span>
          </Label>
        </FormGroup>
      </div>
    </components.Menu>
  );
};
Menu.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  selectProps: PropTypes.shape({
    handleEcosystemToggle: PropTypes.func,
    includeChildren: PropTypes.bool,
    mobileView: PropTypes.bool,
  }),
};

export default memo(SearchByOrgDropdown);
function SearchByOrgDropdown({
  handleEcosystemToggle,
  handleOrgSelected,
  includeChildren,
  mobileView,
  orgID,
  orgIdFilter,
}) {
  const orgsMap = useContext(OrgsMapContext);

  const conditionalMaxMenuHeight = useMemo(
      () => (mobileView ? { maxMenuHeight: 200 } : {}),
      [mobileView]
    ),
    desktopComponents = useMemo(
      () => (mobileView ? {} : { Menu }),
      [mobileView]
    ),
    mobileComponents = useMemo(
      () => (mobileView ? { DropdownIndicator } : {}),
      [mobileView]
    ),
    mobileControlStyles = useMemo(
      () => (mobileView ? { height: '30px', minHeight: '30px' } : {}),
      [mobileView]
    ),
    mobileIndicatorContainerStyles = useMemo(
      () =>
        mobileView ? { paddingTop: '0.25rem', paddingRight: '0.4rem' } : {},
      [mobileView]
    ),
    mobileValueContainerStyles = useMemo(
      () =>
        mobileView
          ? {
              height: '30px',
              minHeight: '30px',
              position: 'static',
              lineHeight: '0.75rem',
            }
          : {},
      [mobileView]
    ),
    optionalBorder = useMemo(
      () =>
        mobileView ? { borderColor: '#dee2e6 !important' } : { border: 'none' },
      [mobileView]
    ),
    options = useMemo(
      () =>
        Object.entries(orgsMap)
          .map(([id, org]) => ({
            label: org.name,
            value: id,
          }))
          .sort((a, b) => a?.label?.localeCompare?.(b?.label)),
      [orgsMap]
    ),
    selectStyles = useMemo(
      () => ({
        control: (base) => ({
          ...base,
          ...optionalBorder,
          ...mobileControlStyles,
          outline: 'none',
          boxShadow: 'none',
        }),
        dropdownIndicator: (base) => ({
          ...base,
          ...mobileIndicatorContainerStyles,
        }),
        indicatorSeparator: (base) => ({ ...base, visibility: 'hidden' }),
        input: (base) => ({ ...base, ...mobileValueContainerStyles }),
        placeholder: (base) => ({
          ...base,
          fontWeight: mobileView ? 'lighter' : 'normal',
          fontSize: '0.75rem',
          color: 'var(--text-gray)',
        }),
        valueContainer: (base) => ({ ...base, ...mobileValueContainerStyles }),
      }),
      [
        mobileControlStyles,
        mobileIndicatorContainerStyles,
        mobileValueContainerStyles,
        mobileView,
        optionalBorder,
      ]
    ),
    value = useMemo(
      () => options.find(({ value }) => value === (orgIdFilter || orgID)),
      [options, orgID, orgIdFilter]
    );

  return (
    <Select
      {...conditionalMaxMenuHeight}
      components={{ ...desktopComponents, ...mobileComponents }}
      handleEcosystemToggle={handleEcosystemToggle}
      includeChildren={includeChildren}
      isClearable
      mobileView={mobileView}
      noOptionsMessage={() => `Search by organization name...`}
      onChange={(selected) => handleOrgSelected(selected, mobileView)}
      options={options}
      placeholder="Search by Organization"
      styles={selectStyles}
      value={value}
    />
  );
}

SearchByOrgDropdown.propTypes = {
  handleEcosystemToggle: PropTypes.func,
  handleOrgSelected: PropTypes.func,
  includeChildren: PropTypes.bool,
  mobileView: PropTypes.bool,
  orgID: PropTypes.string,
  orgIdFilter: PropTypes.string,
};
